/**
 * The renderer. model + layout + action kind → DOM.
 *
 * THE RULE (PRODUCT_CARD_PLAN.md §2): this file and everything it imports
 * fetches nothing, touches no global, and reads no source-shaped field. It is
 * a pure function of the model. That is what lets the whole card be exercised
 * against fixtures with no bench, and what stops the "six scripts patch the
 * DOM after paint" problem from growing back.
 *
 * Anything that needs data goes through an adapter first. Anything that needs
 * an event listener is delegated by the grid.
 */

import { LAYOUT, validateModel } from '../model.js';
import { renderLayout } from './layout/index.js';

const DEFAULT_CTX = {
	layout: LAYOUT.STANDARD,
	columnClass: 'col-sm-4',
	settings: {},
	// Set true in dev to fail loudly on a contract violation instead of
	// rendering a subtly-wrong card.
	strict: false,
};

/**
 * @param {object} model  a ProductCardModel (see model.js)
 * @param {object} [ctx]  { layout, columnClass, settings, strict }
 * @returns {HTMLElement}
 */
export function renderCard(model, ctx) {
	const context = Object.assign({}, DEFAULT_CTX, ctx);

	if (context.strict) {
		const errors = validateModel(model);
		if (errors.length) {
			throw new Error(`renderCard: invalid model\n  - ${errors.join('\n  - ')}`);
		}
	}

	return renderLayout(model, context);
}

/**
 * @param {Array} models
 * @param {object} [ctx]
 * @returns {DocumentFragment}
 */
export function renderCards(models, ctx) {
	const frag = document.createDocumentFragment();
	for (const model of models || []) {
		frag.appendChild(renderCard(model, ctx));
	}
	return frag;
}

/**
 * Re-render one card in place from an updated model.
 *
 * This is how a decoration that arrives after first paint (badges, swatches,
 * a cart-qty change) reaches the DOM: the RENDERER redraws from the model.
 * External code never reaches into a card and mutates it — that distinction
 * is the whole point of the consolidation.
 */
export function replaceCard(cardEl, model, ctx) {
	const next = renderCard(model, ctx);
	if (cardEl && cardEl.parentNode) {
		cardEl.parentNode.replaceChild(next, cardEl);
	}
	return next;
}
